import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Send, CheckCircle2, XCircle, MessageSquare, RotateCcw } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/settings`;
const err = (e, fb) => e.response?.data?.detail || fb;

const BulkSummary = ({ job }) => (
  <div className="border rounded-lg p-3 space-y-2 text-sm" data-testid="helpconnect-bulk-summary">
    {job.status === 'running' ? (
      <p className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Working… {job.processed || 0} of {job.total ?? '…'} done</p>
    ) : job.status === 'error' ? (
      <p className="text-destructive" data-testid="helpconnect-bulk-error">The bulk action stopped: {job.error}</p>
    ) : (
      <p className="font-medium">Finished</p>
    )}
    <p className="flex items-center gap-2" data-testid="helpconnect-bulk-started-count"><CheckCircle2 className="w-4 h-4 text-green-600" />{job.started || 0} {job.mode === 'reminder' ? 'reminded' : 'started'}</p>
    <p className="flex items-center gap-2" data-testid="helpconnect-bulk-failed-count"><XCircle className="w-4 h-4 text-destructive" />{(job.failed || []).length} failed</p>
    {(job.failed || []).length > 0 && (
      <ul className="max-h-48 overflow-y-auto divide-y border rounded" data-testid="helpconnect-bulk-failed-list">
        {job.failed.map((f) => (
          <li key={f.participant_id} className="px-2 py-1" data-testid={`helpconnect-bulk-failed-${f.participant_id}`}>
            <span className="font-medium">{f.name}</span> — {f.reason}
          </li>
        ))}
      </ul>
    )}
    {(job.text_skipped || []).length > 0 && (
      <>
        <p className="flex items-center gap-2" data-testid="helpconnect-bulk-text-skipped-count"><MessageSquare className="w-4 h-4 text-amber-600" />{job.text_skipped.length} did not get the text (email still sent)</p>
        <ul className="max-h-48 overflow-y-auto divide-y border rounded" data-testid="helpconnect-bulk-text-skipped-list">
          {job.text_skipped.map((f) => (
            <li key={f.participant_id} className="px-2 py-1" data-testid={`helpconnect-bulk-text-skipped-${f.participant_id}`}>
              <span className="font-medium">{f.name}</span> — {f.reason}
            </li>
          ))}
        </ul>
      </>
    )}
  </div>
);

const TransitionSmsEditor = () => {
  const [data, setData] = useState(null);
  const [sms, setSms] = useState('');
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    axios.get(`${API}/helpconnect-transition-email`).then((r) => { setData(r.data); setSms(r.data.sms); })
      .catch((e) => toast.error(err(e, 'Failed to load text wording')));
  }, []);
  useEffect(() => {
    if (!data) return undefined;
    const t = setTimeout(() => {
      axios.post(`${API}/helpconnect-transition-sms/preview`, { sms }).then((r) => setPreview(r.data)).catch(() => {});
    }, 400);
    return () => clearTimeout(t);
  }, [sms, data]);
  const save = async () => {
    setSaving(true);
    try { setData((await axios.put(`${API}/helpconnect-transition-sms`, { sms })).data); toast.success('Text wording saved'); }
    catch (e) { toast.error(err(e, 'Failed to save text wording')); }
    finally { setSaving(false); }
  };
  if (!data) return <Loader2 className="w-5 h-5 animate-spin" />;
  return (
    <div className="space-y-2 border-t pt-4" data-testid="helpconnect-transition-sms-box">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium flex items-center gap-2"><MessageSquare className="w-4 h-4" />Text message</label>
        <Button variant="ghost" size="sm" onClick={() => setSms(data.sms_default)} data-testid="helpconnect-transition-sms-restore-btn">
          <RotateCcw className="w-4 h-4 mr-1" />Restore original wording
        </Button>
      </div>
      <Textarea rows={4} value={sms} onChange={(e) => setSms(e.target.value)} data-testid="helpconnect-transition-sms-input" />
      <p className="text-xs text-muted-foreground" data-testid="helpconnect-transition-sms-count">
        {sms.length} characters in your wording{preview ? ` · ${preview.length} characters with sample details and link` : ''}
      </p>
      <div className="rounded-lg border p-3 space-y-1 text-xs" data-testid="helpconnect-transition-sms-fields">
        <p className="font-semibold text-sm">Available fields</p>
        {data.sms_fields.map((f) => (
          <p key={f.token}><code className="bg-muted px-1 rounded">{f.token}</code> <span className="text-muted-foreground">{f.label}</span></p>
        ))}
        <p className="text-muted-foreground">The invitation link is always added, even if you remove it.</p>
      </div>
      {preview && (
        <div className="rounded-lg border bg-muted/30 p-3 text-sm" data-testid="helpconnect-transition-sms-preview">
          <p className="text-xs font-semibold uppercase text-muted-foreground mb-1">Text preview (sample details)</p>
          <p className="whitespace-pre-wrap">{preview.sms}</p>
        </div>
      )}
      <Button variant="outline" onClick={save} disabled={saving} data-testid="helpconnect-transition-sms-save-btn">
        {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Save Text
      </Button>
    </div>
  );
};

const TransitionEmailEditor = () => {
  const [tpl, setTpl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  useEffect(() => { axios.get(`${API}/helpconnect-transition-email`).then((r) => setTpl(r.data)).catch((e) => toast.error(err(e, 'Failed to load email text'))); }, []);
  const save = async () => {
    setSaving(true);
    try { setTpl((await axios.put(`${API}/helpconnect-transition-email`, { subject: tpl.subject, body: tpl.body })).data); toast.success('Email text saved'); }
    catch (e) { toast.error(err(e, 'Failed to save email text')); }
    finally { setSaving(false); }
  };
  const sendTest = async () => {
    setTesting(true);
    try { toast.success(`Test email sent to ${(await axios.post(`${API}/helpconnect-transition-email/test`, { subject: tpl.subject, body: tpl.body })).data.sent_to}`); }
    catch (e) { toast.error(err(e, 'Failed to send test email')); }
    finally { setTesting(false); }
  };
  if (!tpl) return <Loader2 className="w-5 h-5 animate-spin" />;
  return (
    <div className="space-y-2">
      <label className="text-sm font-medium">Email subject</label>
      <Input value={tpl.subject} onChange={(e) => setTpl({ ...tpl, subject: e.target.value })} data-testid="helpconnect-transition-subject-input" />
      <label className="text-sm font-medium">Email message</label>
      <Textarea rows={12} value={tpl.body} onChange={(e) => setTpl({ ...tpl, body: e.target.value })} data-testid="helpconnect-transition-body-input" />
      <p className="text-xs text-muted-foreground">
        {'{{first name}}'} is the participant's first name. {'{{program name}}'} becomes "Pathways to Employability" or "The HELP Institute" plus the course name, from their most recent enrollment. The setup button is added below the message.
      </p>
      <Button variant="outline" onClick={save} disabled={saving} data-testid="helpconnect-transition-save-btn">
        {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Save Email Text
      </Button>
      <Button variant="outline" className="ml-2" onClick={sendTest} disabled={testing} data-testid="helpconnect-transition-test-btn">
        {testing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Send Me a Test
      </Button>
    </div>
  );
};

export const HelpConnectBulkAccessCard = () => {
  const { user } = useAuth();
  const [count, setCount] = useState(null);
  const [mode, setMode] = useState('start');
  const [checking, setChecking] = useState(false);
  const [job, setJob] = useState(null);
  const timer = useRef(null);
  useEffect(() => () => clearInterval(timer.current), []);
  if (user?.role !== 'system_admin') return null;

  const check = async (m) => {
    setChecking(true);
    setMode(m);
    try { setCount((await axios.get(`${API}/helpconnect-bulk-access/preview`, { params: { mode: m } })).data.count); }
    catch (e) { toast.error(err(e, 'Failed to count participants')); }
    finally { setChecking(false); }
  };
  const run = async () => {
    setCount(null);
    try {
      const r = await axios.post(`${API}/helpconnect-bulk-access/run`, null, { params: { mode } });
      setJob(r.data);
      clearInterval(timer.current);
      timer.current = setInterval(async () => {
        try {
          const j = (await axios.get(`${API}/helpconnect-bulk-access/jobs/${r.data.id}`)).data;
          setJob(j);
          if (j.status !== 'running') clearInterval(timer.current);
        } catch (e) { clearInterval(timer.current); toast.error(err(e, 'Lost track of the bulk action')); }
      }, 2000);
    } catch (e) { toast.error(err(e, 'Failed to start the bulk action')); }
  };

  return (
    <Card className="card-shadow" data-testid="helpconnect-bulk-access-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Send className="w-5 h-5" />Start HELPConnect Access for All</CardTitle>
        <CardDescription>
          Sends the HELPConnect invitation (text and email) to every participant with an active Pathways to Employability or HELP Institute case who hasn't set up HELPConnect yet. Closed cases are not included.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <TransitionEmailEditor />
        <TransitionSmsEditor />
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => check('start')} disabled={checking || job?.status === 'running'} data-testid="helpconnect-bulk-start-btn">
            {checking && mode === 'start' && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Start HELPConnect Access
          </Button>
          <Button variant="outline" onClick={() => check('reminder')} disabled={checking || job?.status === 'running'} data-testid="helpconnect-bulk-reminder-btn">
            {checking && mode === 'reminder' && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Resend Invitation Reminder
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">The reminder resends this email to anyone invited at least 7 days ago who still hasn't set up HELPConnect.</p>
        {job && <BulkSummary job={job} />}
      </CardContent>
      <AlertDialog open={count !== null} onOpenChange={(o) => !o && setCount(null)}>
        <AlertDialogContent data-testid="helpconnect-bulk-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{mode === 'reminder' ? 'Resend invitation reminder?' : 'Start HELPConnect access?'}</AlertDialogTitle>
            <AlertDialogDescription data-testid="helpconnect-bulk-confirm-count">
              {count === 0 ? (mode === 'reminder' ? 'No participants need a reminder right now.' : 'No participants need HELPConnect access right now.') : `This will affect ${count} participant${count === 1 ? '' : 's'}.${mode === 'start' ? ' Each will get an email and a text message (the text is skipped for anyone with no phone number or who can\'t receive texts).' : ''}`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="helpconnect-bulk-cancel-btn">Cancel</AlertDialogCancel>
            {count > 0 && <AlertDialogAction onClick={run} data-testid="helpconnect-bulk-confirm-btn">Start</AlertDialogAction>}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};
